// Lightweight replacement for `posthog-js/react`. The official package
// statically imports the entire posthog-js core just to use it as a context
// default, dragging the library into every chunk that reads a flag. This module
// only type-imports posthog-js, so it adds nothing to the bundle. The real
// client is injected at runtime by the lazily-loaded PostHogLoader.
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PostHog } from 'posthog-js';

type PostHogContextT = {
  client: PostHog | null;
  setClient: (client: PostHog) => void;
};

const noop = () => undefined;

const PostHogContext = createContext<PostHogContextT>({
  client: null,
  setClient: noop,
});

export function PostHogRootProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<PostHog | null>(null);
  const value = useMemo(() => ({ client, setClient }), [client]);

  return (
    <PostHogContext.Provider value={value}>{children}</PostHogContext.Provider>
  );
}

// Registers the initialized client; called by the lazy PostHogLoader.
export function useSetPostHogClient() {
  return useContext(PostHogContext).setClient;
}

// `null` until the lazy posthog chunk has loaded and initialized.
export function usePostHog(): PostHog | null {
  return useContext(PostHogContext).client;
}

export function useFeatureFlagEnabled(flag: string): boolean | undefined {
  const client = usePostHog();
  const [enabled, setEnabled] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!client) {
      return undefined;
    }

    setEnabled(client.isFeatureEnabled(flag));

    return client.onFeatureFlags(() => {
      setEnabled(client.isFeatureEnabled(flag));
    });
  }, [client, flag]);

  return enabled;
}
